/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { FormikProps } from 'formik';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { Alert, CircularProgress } from '@mui/material';

import { AlignCenter } from '../../../components/AlignCenter/AlignCenter';
import { SupportPlanCard } from '../../../components/SupportPlanCard/SupportPlanCard';
import { Namespaces } from '../../../i18n';
import { useSupportPlansStyles } from '../../../styles/supportPlans';
import { SystemConfiguration } from '../../../types/superuser/SystemConfiguration';
import { fetchSupportPlans, fetchSystemConfiguration } from '../../../utils/queryFns';
import { CreateClusterValues } from '../CreateClusterOverlay.types';

type Props<Values> = FormikProps<Values>;

export function CreateClusterStepSupportPlan<Values extends CreateClusterValues>({
  values,
  setFieldValue,
}: Props<Values>) {
  const classes = useSupportPlansStyles();
  const { t } = useTranslation([Namespaces.Common, Namespaces.CreateCluster]);
  const {
    data: supportPlansData,
    isLoading,
    isError,
  } = useQuery([fetchSupportPlans.key], fetchSupportPlans.fn, {
    refetchOnMount: true,
  });

  const systemConfigQuery = useQuery([fetchSystemConfiguration.key], fetchSystemConfiguration.fn);

  const systemConfig = useMemo(
    () => systemConfigQuery.data?.payload ?? ({} as SystemConfiguration),
    [systemConfigQuery.data],
  );

  const supportPlans = useMemo(
    () => (supportPlansData && !!supportPlansData?.payload.length ? supportPlansData.payload : []),
    [supportPlansData],
  );

  const defaultSupportPlan = useMemo(() => supportPlans.find(plan => plan.isDefault), [supportPlans]);

  const handleSupportPlanChange = useCallback(
    (supportPlanId: string) => setFieldValue('supportPlanId', supportPlanId),
    [setFieldValue],
  );

  useEffect(() => {
    if (!values.supportPlanId && defaultSupportPlan) {
      void handleSupportPlanChange(defaultSupportPlan.id);
    }
  }, [values, defaultSupportPlan, handleSupportPlanChange]);

  const keyListener = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.code === 'Space') {
      e.preventDefault();
      const activeElement = document.activeElement;
      if (activeElement) {
        const [name, id] = activeElement.id.split('-');
        const plan = supportPlans.find(plan => plan.name === name && plan.id === id);
        plan && void handleSupportPlanChange(plan.id);
      }
    }
  };

  if (isLoading) {
    return (
      <AlignCenter className={classes.loaderContainer}>
        <CircularProgress color="primary" size={40} />
      </AlignCenter>
    );
  }

  if (isError) {
    return <Alert severity="error">{t(`${Namespaces.Common}:errors.unexpected_error`)}</Alert>;
  }

  return (
    <div className={classes.root} onKeyDown={keyListener}>
      <div className={classes.title}>{t(`${Namespaces.CreateCluster}:labels.choose_support_plans`)}</div>
      <div className={classes.supportPlansContainer}>
        <SupportPlanCard
          supportPlans={supportPlans}
          displayAmount={systemConfig.showSupportCost}
          currency={systemConfig.currency}
          selectedSupportPlanId={values.supportPlanId}
          onChange={handleSupportPlanChange}
        />
      </div>
    </div>
  );
}
